import { renderLayer } from '../animation/engine';
import { computeAt } from '../animation/keyframes';
import {
  addStroke,
  appendStrokePoint,
  getState,
  removeGuide,
  setDisplayScale,
  setEditingId,
  setSelectedGuideId,
  setSelectedId,
  setSelectedIds,
  setZoom,
  subscribe,
  updateGuide,
  updateLayer,
  updateLayerTransform,
} from '../store/project';
import { openContextMenu } from '../ui/ContextMenu';
import { buildCanvasEmptyMenu, buildLayerMenu } from '../ui/menus';
import { RATIO_DIMS } from '../types';
import type { Background, Layer, Project } from '../types';

function getCanvasDims(p: Project): { w: number; h: number } {
  return p.canvas.customSize ?? RATIO_DIMS[p.canvas.ratio];
}

// 4 corners (diagonal resize) + 4 edges (single-axis resize).
type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

interface DragSession {
  layerId: string;
  mode: 'move' | 'resize';
  handle?: ResizeHandle;
  startMouseX: number;
  startMouseY: number;
  origX: number;
  origY: number;
  origW: number;
  origH: number;
  scale: number; // canvas-px per screen-px
  // Pending values are applied directly to the visible DOM during drag and
  // only flushed to the store on mouseup. This avoids a per-frame re-render
  // (which used to steal focus from the color picker and hammer the canvas).
  pendingX: number;
  pendingY: number;
  pendingW: number;
  pendingH: number;
}

interface LayerNode {
  wrap: HTMLElement;
  inner: HTMLElement;
  /** Overlay sits absolutely above `inner`'s content. Used for color /
   *  gradient overlay effects. `display: none` when no overlay. */
  overlay: HTMLElement;
}

export class Canvas {
  private root: HTMLElement;
  private frameWrap!: HTMLElement;
  private frame!: HTMLElement;
  private safeEl!: HTMLElement;
  private safeLabel!: HTMLElement;
  private bgEl!: HTMLElement;
  private layerHost!: HTMLElement;
  private countdownEl!: HTMLElement;
  private countNum!: HTMLElement;
  private rulerH!: HTMLCanvasElement;
  private rulerV!: HTMLCanvasElement;
  private rulerCorner!: HTMLElement;
  private lassoBox!: HTMLElement;
  private dragSession: DragSession | null = null;
  private currentScale = 1;
  private panX = 0;
  private panY = 0;
  private panDrag: { startMouseX: number; startMouseY: number; origX: number; origY: number } | null = null;
  private lassoDrag: { startX: number; startY: number; pendingRect: DOMRect | null } | null = null;
  private layerNodes = new Map<string, LayerNode>();
  private bgVideo: HTMLVideoElement | null = null;
  private bgVideoSrc: string | null = null;
  private guideHost!: HTMLElement;
  private guideNodes = new Map<string, HTMLElement>();

  constructor(root: HTMLElement) {
    this.root = root;
    this.build();
    subscribe((s) => this.render(s.project, s.playhead, s.selectedId, s.recordMode, s.selectedIds));
    window.addEventListener('resize', () => this.fit());
    this.attachDrag();
  }

  private build(): void {
    while (this.root.firstChild) this.root.removeChild(this.root.firstChild);

    this.frameWrap = el('div', { className: 'frame-wrap' });
    this.safeLabel = el('div', { className: 'safe-label' });
    this.frame = el('div', { className: 'frame' });
    this.bgEl = el('div', {
      className: 'bg-host',
      style: { position: 'absolute', inset: '0' },
    });
    this.layerHost = el('div', {
      style: { position: 'absolute', inset: '0' },
    });
    this.safeEl = el('div', { className: 'safe' });
    this.countdownEl = el('div', { className: 'countdown' });
    this.countNum = el('div', { className: 'num', text: '3' });
    this.countdownEl.appendChild(this.countNum);

    this.guideHost = el('div', {
      className: 'guide-host',
      style: { position: 'absolute', inset: '0', pointerEvents: 'none' },
    });
    this.frame.append(this.bgEl, this.layerHost, this.safeEl, this.guideHost, this.countdownEl);
    this.frameWrap.append(this.safeLabel, this.frame);
    this.root.appendChild(this.frameWrap);

    // ─── Rulers (Photoshop-style; px in canvas coords) ───
    this.rulerH = document.createElement('canvas');
    this.rulerH.className = 'canvas-ruler ruler-h';
    this.rulerV = document.createElement('canvas');
    this.rulerV.className = 'canvas-ruler ruler-v';
    this.rulerCorner = document.createElement('div');
    this.rulerCorner.className = 'canvas-ruler ruler-corner';
    this.root.append(this.rulerH, this.rulerV, this.rulerCorner);

    // Lasso selection box (lives on the stage, drawn in viewport coords)
    this.lassoBox = document.createElement('div');
    this.lassoBox.className = 'lasso-box';
    this.lassoBox.style.display = 'none';
    this.root.appendChild(this.lassoBox);

    // Drive cursor + behaviour from the active tool
    subscribe((s) => {
      this.root.classList.toggle('tool-pan', s.tool === 'pan');
      this.root.classList.toggle('tool-lasso', s.tool === 'lasso');
    });

    // Pan + lasso interactions live on the stage, not on the frame, so they
    // work in the surrounding gutter too.
    // Capture phase so lasso / pan can hijack the gesture before the
    // frame / layer mousedown listeners get to run (otherwise they'd start
    // a drag-to-move session or clear the selection).
    this.root.addEventListener(
      'mousedown',
      (e) => {
        if (e.button !== 0) return;
        const tool = getState().tool;
        if (tool === 'pan') {
          e.stopPropagation();
          e.preventDefault();
          this.panDrag = {
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            origX: this.panX,
            origY: this.panY,
          };
          return;
        }
        if (tool === 'lasso') {
          e.stopPropagation();
          e.preventDefault();
          const rect = this.root.getBoundingClientRect();
          this.lassoDrag = {
            startX: e.clientX - rect.left,
            startY: e.clientY - rect.top,
            pendingRect: null,
          };
          this.lassoBox.style.display = 'block';
          this.lassoBox.style.left = `${this.lassoDrag.startX}px`;
          this.lassoBox.style.top = `${this.lassoDrag.startY}px`;
          this.lassoBox.style.width = '0';
          this.lassoBox.style.height = '0';
        }
      },
      true,
    );

    window.addEventListener('mousemove', (e) => {
      if (this.panDrag) {
        const dx = e.clientX - this.panDrag.startMouseX;
        const dy = e.clientY - this.panDrag.startMouseY;
        this.panX = this.panDrag.origX + dx;
        this.panY = this.panDrag.origY + dy;
        this.frameWrap.style.transform = `translate(${this.panX}px, ${this.panY}px)`;
        // Rulers track the frame
        this.fit();
        return;
      }
      if (this.lassoDrag) {
        const rect = this.root.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const x = Math.min(this.lassoDrag.startX, cx);
        const y = Math.min(this.lassoDrag.startY, cy);
        const w = Math.abs(cx - this.lassoDrag.startX);
        const h = Math.abs(cy - this.lassoDrag.startY);
        this.lassoBox.style.left = `${x}px`;
        this.lassoBox.style.top = `${y}px`;
        this.lassoBox.style.width = `${w}px`;
        this.lassoBox.style.height = `${h}px`;
        this.lassoDrag.pendingRect = new DOMRect(rect.left + x, rect.top + y, w, h);
      }
    });

    window.addEventListener('mouseup', () => {
      if (this.panDrag) {
        this.panDrag = null;
        return;
      }
      if (this.lassoDrag) {
        const rect = this.lassoDrag.pendingRect;
        this.lassoDrag = null;
        this.lassoBox.style.display = 'none';
        if (rect && rect.width > 4 && rect.height > 4) {
          // Find every .layer whose bbox intersects the lasso rect.
          const ids: string[] = [];
          for (const el of this.layerHost.querySelectorAll<HTMLElement>('.layer')) {
            const id = el.dataset.id;
            if (!id) continue;
            const lr = el.getBoundingClientRect();
            if (lr.right < rect.left || lr.left > rect.right) continue;
            if (lr.bottom < rect.top || lr.top > rect.bottom) continue;
            ids.push(id);
          }
          setSelectedIds(ids);
        }
      }
    });

    // Wheel = zoom (vertical), trackpad horizontal swipe (deltaX) is left
    // alone for users who scroll the stage.
    this.root.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      setZoom(getState().zoom * factor);
    }, { passive: false });

    this.frame.addEventListener('mousedown', (e) => {
      if (e.target === this.frame || e.target === this.bgEl || e.target === this.layerHost) {
        setSelectedId(null);
      }
    });

    this.frame.addEventListener('contextmenu', (e) => {
      const target = e.target as HTMLElement;
      const layerEl = target.closest('.layer') as HTMLElement | null;
      if (layerEl?.dataset.id) {
        const id = layerEl.dataset.id;
        setSelectedId(id);
        openContextMenu(e, buildLayerMenu(id));
        return;
      }
      openContextMenu(e, buildCanvasEmptyMenu());
    });

    this.frame.addEventListener('dblclick', (e) => {
      const target = e.target as HTMLElement;
      const layerEl = target.closest('.layer') as HTMLElement | null;
      if (!layerEl) return;
      const id = layerEl.dataset.id;
      if (!id) return;
      const layer = getState().project.layers.find((l) => l.id === id);
      if (!layer || layer.kind !== 'text') return;
      e.preventDefault();
      e.stopPropagation();
      setEditingId(id);
    });
  }

  private fit(): void {
    const { project, zoom } = getState();
    const dims = getCanvasDims(project);
    const stagePadding = 32;
    const availW = this.root.clientWidth - stagePadding * 2;
    const availH = this.root.clientHeight - stagePadding * 2;
    const fitScale = Math.min(availW / dims.w, availH / dims.h, 1);
    const scale = fitScale * zoom;
    this.currentScale = scale;
    setDisplayScale(scale);
    this.frameWrap.style.width = `${dims.w * scale}px`;
    this.frameWrap.style.height = `${dims.h * scale}px`;
    this.frame.style.width = `${dims.w}px`;
    this.frame.style.height = `${dims.h}px`;
    this.frame.style.transformOrigin = 'top left';
    this.frame.style.transform = `scale(${scale})`;
    this.drawRulers(scale, dims);
  }

  /** Position the rulers along the frameWrap edges and paint px ticks. */
  private drawRulers(scale: number, dims: { w: number; h: number }): void {
    const wrapRect = this.frameWrap.getBoundingClientRect();
    const rootRect = this.root.getBoundingClientRect();
    const left = wrapRect.left - rootRect.left;
    const top = wrapRect.top - rootRect.top;
    const RUL = 18;

    this.rulerCorner.style.left = `${left - RUL}px`;
    this.rulerCorner.style.top = `${top - RUL}px`;
    this.rulerCorner.style.width = `${RUL}px`;
    this.rulerCorner.style.height = `${RUL}px`;

    this.rulerH.style.left = `${left}px`;
    this.rulerH.style.top = `${top - RUL}px`;
    this.rulerH.style.width = `${dims.w * scale}px`;
    this.rulerH.style.height = `${RUL}px`;

    this.rulerV.style.left = `${left - RUL}px`;
    this.rulerV.style.top = `${top}px`;
    this.rulerV.style.width = `${RUL}px`;
    this.rulerV.style.height = `${dims.h * scale}px`;

    paintRuler(this.rulerH, dims.w, scale, 'horizontal');
    paintRuler(this.rulerV, dims.h, scale, 'vertical');
  }

  private renderBackground(bg: Background): void {
    if (this.bgVideo && (bg.kind !== 'video' || this.bgVideoSrc !== bg.src)) {
      this.bgVideo.remove();
      this.bgVideo = null;
      this.bgVideoSrc = null;
    }

    if (bg.kind === 'color') {
      this.bgEl.style.background = bg.color;
    } else if (bg.kind === 'gradient') {
      this.bgEl.style.background = `linear-gradient(${bg.angle}deg, ${bg.from}, ${bg.to})`;
    } else if (bg.kind === 'image') {
      this.bgEl.style.background = `url("${escapeUrl(bg.src)}") center/${bg.fit} no-repeat #000`;
    } else if (bg.kind === 'video') {
      this.bgEl.style.background = '#000';
      if (!this.bgVideo) {
        const v = document.createElement('video');
        v.muted = bg.muted;
        v.loop = bg.loop;
        v.playsInline = true;
        v.style.position = 'absolute';
        v.style.inset = '0';
        v.style.width = '100%';
        v.style.height = '100%';
        v.style.objectFit = bg.fit;
        v.src = bg.src;
        v.play().catch(() => {});
        this.bgEl.appendChild(v);
        this.bgVideo = v;
        this.bgVideoSrc = bg.src;
      }
      const playhead = getState().playhead;
      if (this.bgVideo && !getState().isPlaying) {
        try {
          if (Math.abs(this.bgVideo.currentTime * 1000 - playhead) > 200) {
            this.bgVideo.currentTime = playhead / 1000;
          }
        } catch {
          // ignore
        }
      }
    }
  }

  private renderSafeZone(project: Project): void {
    const sr = project.canvas.safeRatio;
    if (!sr) {
      this.safeEl.style.display = 'none';
      this.safeLabel.style.display = 'none';
      return;
    }
    this.safeEl.style.display = 'block';
    const dims = getCanvasDims(project);
    const safeDims = RATIO_DIMS[sr];
    const safeAR = safeDims.w / safeDims.h;
    const canvasAR = dims.w / dims.h;
    let w = dims.w;
    let h = dims.h;
    if (safeAR > canvasAR) h = dims.w / safeAR;
    else w = dims.h * safeAR;
    this.safeEl.style.left = `${(dims.w - w) / 2}px`;
    this.safeEl.style.top = `${(dims.h - h) / 2}px`;
    this.safeEl.style.width = `${w}px`;
    this.safeEl.style.height = `${h}px`;
    this.safeLabel.style.display = 'block';
    this.safeLabel.textContent = `Safe ${sr}`;
  }

  private renderLayers(
    project: Project,
    t: number,
    selectedId: string | null,
    recordMode: boolean,
    selectedIds: string[],
  ): void {
    const seen = new Set<string>();
    const multi = new Set(selectedIds);
    project.layers.forEach((layer, i) => {
      seen.add(layer.id);
      let entry = this.layerNodes.get(layer.id);
      if (!entry) entry = this.createLayerNode(layer.id);
      this.applyLayer(layer, t, entry, selectedId === layer.id, recordMode);
      entry.wrap.classList.toggle(
        'multi-selected',
        multi.has(layer.id) && layer.id !== selectedId && multi.size > 1,
      );
      entry.wrap.style.zIndex = String(i + 1);
    });
    for (const [id, entry] of this.layerNodes) {
      if (!seen.has(id)) {
        entry.wrap.remove();
        this.layerNodes.delete(id);
      }
    }
  }

  private createLayerNode(id: string): LayerNode {
    const wrap = el('div', { className: 'layer' });
    wrap.dataset.id = id;
    const inner = el('div', {
      style: { width: '100%', height: '100%', position: 'relative' },
    });
    // Overlay sits absolutely above the inner content. pointer-events: none
    // so it doesn't steal drags from the layer surface (handles, text edit).
    const overlay = el('div', {
      className: 'layer-overlay',
      style: {
        position: 'absolute',
        inset: '0',
        pointerEvents: 'none',
        display: 'none',
      },
    });
    inner.appendChild(overlay);
    wrap.appendChild(inner);
    const HANDLES: ResizeHandle[] = ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];
    for (const dir of HANDLES) {
      const h = el('div', { className: `handle ${dir}` });
      h.dataset.handle = dir;
      h.dataset.id = id;
      wrap.appendChild(h);
    }
    this.layerHost.appendChild(wrap);
    const entry: LayerNode = { wrap, inner, overlay };
    this.layerNodes.set(id, entry);
    return entry;
  }

  private applyLayer(
    layer: Layer,
    t: number,
    entry: LayerNode,
    selected: boolean,
    recordMode: boolean,
  ): void {
    // A selected layer must stay grabbable on the canvas even when the
    // playhead sits outside its [start,end] — otherwise you can only edit it
    // while the playline overlaps its timeline bar. Render it at the bar's
    // midpoint (past enter / before exit, so it's fully present) with handles.
    const outOfRange = t < layer.start || t > layer.end;
    const editOutOfRange = selected && !recordMode && layer.visible !== false && outOfRange;
    const tEff = editOutOfRange ? (layer.start + layer.end) / 2 : t;
    // Resolve keyframe-driven values first; pass the result to the existing
    // entry/exit transition engine on top.
    const effective = computeAt(layer, tEff);
    const r = renderLayer(effective, tEff);
    layer = effective;
    const { wrap, inner } = entry;
    wrap.classList.toggle('selected', selected && !recordMode);
    wrap.style.left = `${layer.transform.x}px`;
    wrap.style.top = `${layer.transform.y}px`;
    wrap.style.width = `${layer.transform.w}px`;
    wrap.style.height = `${layer.transform.h}px`;
    if (!r.visible) {
      wrap.style.display = 'none';
      return;
    }
    wrap.style.display = 'block';
    Object.assign(wrap.style, r.wrapperStyle);

    wrap.querySelectorAll<HTMLElement>('.handle').forEach((h) => {
      h.style.display = selected && !recordMode ? 'block' : 'none';
    });

    inner.removeAttribute('style');
    inner.style.width = '100%';
    inner.style.height = '100%';
    inner.style.position = 'relative';
    Object.assign(inner.style, r.innerStyle);

    // Apply the overlay style (color / gradient overlay). We pop the
    // overlay out before the type-specific applyXLayer mutates `inner`'s
    // children, then re-attach it as the LAST child so it sits visually
    // above the content.
    const overlay = entry.overlay;
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);

    switch (layer.kind) {
      case 'text':
        this.applyTextLayer(layer, inner, r.strikeProgress);
        break;
      case 'image':
        this.applyImageLayer(layer, inner);
        break;
      case 'video':
        this.applyVideoLayer(layer, inner, t);
        break;
      case 'shape':
        this.applyShapeLayer(layer, inner);
        break;
      case 'drawing':
        this.applyDrawingLayer(layer, inner);
        break;
    }

    // Re-attach overlay as the last child of inner so it draws above the
    // content. Reset its style each frame and apply the engine's output.
    overlay.removeAttribute('style');
    overlay.style.position = 'absolute';
    overlay.style.inset = '0';
    overlay.style.pointerEvents = 'none';
    Object.assign(overlay.style, r.overlayStyle);
    inner.appendChild(overlay);

    // Per-layer escape hatches (ActionScript-style).
    if (layer.customCss) {
      try {
        // Append to inline style; final declarations win.
        inner.style.cssText = inner.style.cssText + ';' + layer.customCss;
      } catch {
        // ignore parse errors
      }
    }
    if (layer.customScript) {
      runLayerScript(layer.customScript, t, layer, inner);
    }
  }

  private applyDrawingLayer(layer: Extract<Layer, { kind: 'drawing' }>, host: HTMLElement): void {
    host.replaceChildren();
    const SVG_NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(SVG_NS, 'svg') as SVGElement;
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('viewBox', `0 0 ${layer.transform.w} ${layer.transform.h}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.display = 'block';
    svg.style.overflow = 'visible';
    const brush = layer.brush ?? {};
    const cap = brush.shape ?? 'round';
    for (const stroke of layer.strokes) {
      if (stroke.points.length < 2) continue;
      const path = document.createElementNS(SVG_NS, 'path') as SVGElement;
      path.setAttribute('d', strokeToPath(stroke.points));
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', brush.color ?? stroke.color);
      path.setAttribute('stroke-width', String(brush.width ?? stroke.width));
      path.setAttribute('stroke-linecap', cap);
      path.setAttribute('stroke-linejoin', cap === 'round' ? 'round' : 'miter');
      svg.appendChild(path);
    }
    host.appendChild(svg);
  }

  private applyTextLayer(
    layer: Extract<Layer, { kind: 'text' }>,
    host: HTMLElement,
    strikeProgress: number,
  ): void {
    host.replaceChildren();
    if (layer.enter?.type === 'strike') {
      host.style.background = layer.color;
      host.style.borderRadius = '6px';
      host.style.width = `${strikeProgress * 100}%`;
      host.style.boxShadow = `0 2px 12px ${layer.color}55`;
      return;
    }

    // Inline editing: render a textarea matching the text style.
    if (getState().editingId === layer.id) {
      const ta = document.createElement('textarea');
      ta.value = layer.text;
      ta.spellcheck = false;
      Object.assign(ta.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        background: 'transparent',
        border: '2px dashed var(--accent)',
        outline: 'none',
        padding: '0',
        margin: '0',
        resize: 'none',
        fontFamily: layer.fontFamily,
        fontWeight: String(layer.fontWeight),
        fontSize: `${layer.fontSize}px`,
        color: layer.color,
        letterSpacing: `${layer.letterSpacing}px`,
        lineHeight: String(layer.lineHeight),
        textAlign: layer.align,
        whiteSpace: 'pre-wrap',
        boxSizing: 'border-box',
      } satisfies Partial<CSSStyleDeclaration>);
      ta.addEventListener('input', () => {
        // Update the layer text live but keep editing mode (avoid re-mounting via store change)
        updateLayer(layer.id, { text: ta.value });
        // restore caret + focus after re-render
        const cur = ta.selectionStart;
        const end = ta.selectionEnd;
        queueMicrotask(() => {
          const next = host.querySelector('textarea') as HTMLTextAreaElement | null;
          if (next) {
            next.focus();
            next.setSelectionRange(cur, end);
          }
        });
      });
      ta.addEventListener('blur', () => setEditingId(null));
      ta.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          setEditingId(null);
        }
        e.stopPropagation();
      });
      ta.addEventListener('mousedown', (e) => e.stopPropagation());
      ta.addEventListener('dblclick', (e) => e.stopPropagation());
      host.appendChild(ta);
      // Focus on next tick so the freshly-mounted node receives it
      queueMicrotask(() => {
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
      });
      return;
    }

    const span = document.createElement('div');
    span.style.width = '100%';
    span.style.height = '100%';
    span.style.display = 'flex';
    span.style.alignItems = 'center';
    span.style.justifyContent =
      layer.align === 'left' ? 'flex-start' : layer.align === 'right' ? 'flex-end' : 'center';
    span.style.textAlign = layer.align;
    span.style.fontFamily = layer.fontFamily;
    span.style.fontWeight = String(layer.fontWeight);
    span.style.fontSize = `${layer.fontSize}px`;
    span.style.color = layer.color;
    span.style.letterSpacing = `${layer.letterSpacing}px`;
    span.style.lineHeight = String(layer.lineHeight);
    span.style.whiteSpace = 'pre-wrap';
    span.textContent = layer.text;
    host.appendChild(span);
  }

  private applyImageLayer(layer: Extract<Layer, { kind: 'image' }>, host: HTMLElement): void {
    let img = host.querySelector('img') as HTMLImageElement | null;
    if (!img) {
      host.replaceChildren();
      img = document.createElement('img');
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.display = 'block';
      host.appendChild(img);
    }
    if (img.src !== layer.src && layer.src) img.src = layer.src;
    img.style.objectFit = layer.fit;
  }

  private applyVideoLayer(layer: Extract<Layer, { kind: 'video' }>, host: HTMLElement, t: number): void {
    let video = host.querySelector('video') as HTMLVideoElement | null;
    if (!video) {
      host.replaceChildren();
      video = document.createElement('video');
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.display = 'block';
      video.playsInline = true;
      host.appendChild(video);
    }
    if (video.src !== layer.src && layer.src) video.src = layer.src;
    video.muted = layer.muted;
    video.loop = layer.loop;
    video.style.objectFit = layer.fit;

    const localT = (t - layer.start) / 1000;
    if (getState().isPlaying) {
      if (video.paused) video.play().catch(() => {});
    } else {
      if (!video.paused) video.pause();
      try {
        if (Math.abs(video.currentTime - localT) > 0.1) {
          video.currentTime = Math.max(0, localT);
        }
      } catch {
        // ignore
      }
    }
  }

  private applyShapeLayer(layer: Extract<Layer, { kind: 'shape' }>, host: HTMLElement): void {
    host.replaceChildren();
    host.style.background = 'transparent';
    host.style.border = '0';

    const SVG_NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(SVG_NS, 'svg') as SVGElement;
    // width/height as ATTRIBUTES too — some browsers ignore CSS sizing on inline SVG
    // when no width/height attributes are present and the viewBox alone is given.
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.display = 'block';
    svg.style.overflow = 'visible';

    const node = buildShapeNode(layer.shape, SVG_NS);
    if (layer.shape === 'line') {
      node.setAttribute('fill', 'none');
      node.setAttribute('stroke', layer.fill);
      node.setAttribute('stroke-width', String(Math.max(2, layer.strokeWidth || 4)));
      node.setAttribute('stroke-linecap', 'round');
      node.setAttribute('vector-effect', 'non-scaling-stroke');
    } else {
      node.setAttribute('fill', layer.fill || 'none');
      node.setAttribute('stroke', layer.stroke || 'none');
      node.setAttribute('stroke-width', String(layer.strokeWidth || 0));
      node.setAttribute('vector-effect', 'non-scaling-stroke');
      node.setAttribute('stroke-linejoin', 'round');
    }
    svg.appendChild(node);
    host.appendChild(svg);
  }

  private attachDrag(): void {
    // Free-hand drawing — when tool === 'draw', any mousedown on the frame starts a stroke.
    this.frame.addEventListener('mousedown', (e) => {
      const s = getState();
      if (s.tool !== 'draw') return;
      e.preventDefault();
      e.stopPropagation();
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') return;
      const pt = this.toCanvasCoords(e);
      let layerId = s.selectedId;
      const sel = layerId ? s.project.layers.find((l) => l.id === layerId) : null;
      if (!sel || sel.kind !== 'drawing') layerId = null;
      const stroke = {
        points: [pt.x, pt.y],
        color: s.penColor,
        width: s.penWidth,
      };
      const id = addStroke(layerId, stroke);
      setSelectedId(id);
      const onMove = (ev: MouseEvent) => {
        const p = this.toCanvasCoords(ev);
        appendStrokePoint(id, p.x, p.y);
      };
      const onUp = (): void => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });

    this.frame.addEventListener('mousedown', (e) => {
      const target = e.target as HTMLElement;
      if (getState().tool === 'draw') return; // handled above
      // ignore mousedown that originates inside a textarea (inline editing)
      if (target.tagName === 'TEXTAREA') return;
      const handleEl = target.closest('.handle') as HTMLElement | null;
      const layerEl = target.closest('.layer') as HTMLElement | null;
      const st = getState();

      // Sticky mode — the currently-selected layer is what gets dragged
      // regardless of which layer (if any) is under the cursor. Resize
      // handles still target their owner layer (the one whose corner
      // was actually grabbed) so resize stays predictable.
      let id: string | null = null;
      if (st.selectMode === 'sticky' && st.selectedId && !handleEl) {
        id = st.selectedId;
      } else if (layerEl) {
        id = layerEl.dataset.id ?? null;
      }
      if (!id) return;
      const layer = st.project.layers.find((l) => l.id === id);
      if (!layer) return;
      if (st.editingId === id) return; // inline edit takes priority
      setSelectedId(id);
      e.preventDefault();
      e.stopPropagation();
      const handle = handleEl?.dataset.handle as DragSession['handle'] | undefined;
      this.dragSession = {
        layerId: id,
        mode: handle ? 'resize' : 'move',
        handle,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        origX: layer.transform.x,
        origY: layer.transform.y,
        origW: layer.transform.w,
        origH: layer.transform.h,
        pendingX: layer.transform.x,
        pendingY: layer.transform.y,
        pendingW: layer.transform.w,
        pendingH: layer.transform.h,
        scale: this.currentScale,
      };
    });

    window.addEventListener('mousemove', (e) => {
      const s = this.dragSession;
      if (!s) return;
      const dx = (e.clientX - s.startMouseX) / s.scale;
      const dy = (e.clientY - s.startMouseY) / s.scale;
      let nx = s.origX, ny = s.origY, nw = s.origW, nh = s.origH;
      if (s.mode === 'move') {
        nx = s.origX + dx;
        ny = s.origY + dy;
      } else if (s.handle) {
        const { origX, origY, origW, origH } = s;
        const handle = s.handle;
        // East/west drive width, north/south drive height. Edge handles only
        // carry one axis (sign 0 on the other → that dim + position untouched),
        // so they resize a single side freely. Corners carry both.
        const xSign = handle.includes('e') ? +1 : handle.includes('w') ? -1 : 0;
        const ySign = handle.includes('s') ? +1 : handle.includes('n') ? -1 : 0;
        const isCorner = xSign !== 0 && ySign !== 0;
        nw = xSign !== 0 ? Math.max(20, origW + xSign * dx) : origW;
        nh = ySign !== 0 ? Math.max(20, origH + ySign * dy) : origH;
        // Corners keep aspect ratio (Shift frees it); edges are always free
        // single-axis so the user can stretch width or height independently.
        if (isCorner && !e.shiftKey && origW > 0 && origH > 0) {
          const aspect = origW / origH;
          const dW = Math.abs(nw - origW) / origW;
          const dH = Math.abs(nh - origH) / origH;
          if (dW >= dH) nh = nw / aspect;
          else nw = nh * aspect;
          if (nw < 20) { nw = 20; nh = nw / aspect; }
          if (nh < 20) { nh = 20; nw = nh * aspect; }
        }
        nx = origX + xSign * (nw - origW) / 2;
        ny = origY + ySign * (nh - origH) / 2;
      }
      // Snap to guides — Alt held bypasses. Move snaps the bounding box's
      // left / centre / right edges to vertical guides and the top / centre
      // / bottom to horizontal ones, taking the closest within 6 canvas-px.
      if (!e.altKey) {
        const snapped = snapToGuides(getState().project.guides ?? [], nx, ny, nw, nh, s.mode === 'resize' ? s.handle : null);
        nx = snapped.x;
        ny = snapped.y;
      }
      s.pendingX = nx;
      s.pendingY = ny;
      s.pendingW = nw;
      s.pendingH = nh;
      // Update the visible DOM directly — no store write yet, so no global
      // re-render fires (color picker keeps focus, inspector stays put,
      // history doesn't get spammed).
      const node = this.layerNodes.get(s.layerId);
      if (node) {
        node.wrap.style.left = `${nx}px`;
        node.wrap.style.top = `${ny}px`;
        node.wrap.style.width = `${nw}px`;
        node.wrap.style.height = `${nh}px`;
      }
    });

    window.addEventListener('mouseup', () => {
      const s = this.dragSession;
      this.dragSession = null;
      if (!s) return;
      // Flush the final pending values to the store. One emit, one history
      // entry, one render — instead of dozens per second.
      if (
        s.pendingX !== s.origX ||
        s.pendingY !== s.origY ||
        s.pendingW !== s.origW ||
        s.pendingH !== s.origH
      ) {
        updateLayerTransform(s.layerId, {
          x: s.pendingX,
          y: s.pendingY,
          w: s.pendingW,
          h: s.pendingH,
        });
      }
    });
  }

  /** Convert a mouse event into canvas-local (logical) coordinates. */
  private toCanvasCoords(e: MouseEvent): { x: number; y: number } {
    const rect = this.frame.getBoundingClientRect();
    const x = (e.clientX - rect.left) / this.currentScale;
    const y = (e.clientY - rect.top) / this.currentScale;
    return { x, y };
  }

  /** The frame element — the visible canvas surface. Used for region capture. */
  getFrameElement(): HTMLElement {
    return this.frame;
  }

  showCountdown(num: number | null): void {
    if (num === null) {
      this.countdownEl.classList.remove('show');
      return;
    }
    this.countdownEl.classList.add('show');
    this.countNum.textContent = String(num);
  }

  render(
    project: Project,
    t: number,
    selectedId: string | null,
    recordMode: boolean,
    selectedIds: string[] = selectedId ? [selectedId] : [],
  ): void {
    this.fit();
    this.renderBackground(project.canvas.background);
    this.renderSafeZone(project);
    this.renderLayers(project, t, selectedId, recordMode, selectedIds);
    this.renderGuides(project);
  }

  /** Render Photoshop-style guide lines. Existing nodes are reused so an
      in-progress drag never loses its DOM target mid-gesture. */
  private renderGuides(project: Project): void {
    const guides = project.guides ?? [];
    const seen = new Set<string>();
    const dims = getCanvasDims(project);
    for (const g of guides) {
      seen.add(g.id);
      let node = this.guideNodes.get(g.id);
      if (!node) {
        node = document.createElement('div');
        node.className = `canvas-guide canvas-guide-${g.orientation}`;
        node.dataset.id = g.id;
        node.title = '드래그로 이동, 더블클릭으로 삭제';
        this.attachGuideDrag(node);
        this.guideHost.appendChild(node);
        this.guideNodes.set(g.id, node);
      }
      // Position in canvas-local px (0..dims.w / 0..dims.h).
      const w = Math.max(1, g.width ?? 1);
      const color = g.color ?? '#5cd0ff';
      if (g.orientation === 'vertical') {
        const left = (g.position / dims.w) * 100;
        node.style.left = `${left}%`;
        node.style.top = '0';
        node.style.width = '0';
        node.style.height = '100%';
        node.style.borderLeft = `${w}px solid ${color}`;
        node.style.borderTop = '';
      } else {
        const top = (g.position / dims.h) * 100;
        node.style.top = `${top}%`;
        node.style.left = '0';
        node.style.width = '100%';
        node.style.height = '0';
        node.style.borderTop = `${w}px solid ${color}`;
        node.style.borderLeft = '';
      }
      node.dataset.orientation = g.orientation;
      // Visibility — hidden guides still snap (Canvas drag reads them
      // straight from project.guides) but don't paint.
      const isVisible = g.visible !== false;
      node.style.display = isVisible ? '' : 'none';
      // Selected highlight
      const isSelected = getState().selectedGuideId === g.id;
      node.classList.toggle('selected', isSelected);
    }
    // Reap removed guides
    for (const [id, node] of this.guideNodes) {
      if (!seen.has(id)) {
        node.remove();
        this.guideNodes.delete(id);
      }
    }
  }

  private attachGuideDrag(node: HTMLElement): void {
    node.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const id = node.dataset.id;
      const orient = node.dataset.orientation as 'vertical' | 'horizontal' | undefined;
      if (!id || !orient) return;
      // Selecting the guide opens the inspector panel — same gesture as
      // the drag, so a quick click without movement still selects.
      setSelectedGuideId(id);
      const dims = getCanvasDims(getState().project);
      const frameRect = this.frame.getBoundingClientRect();
      node.classList.add('dragging');
      let pendingPos = orient === 'vertical' ? dims.w / 2 : dims.h / 2;
      const onMove = (ev: PointerEvent): void => {
        const px = orient === 'vertical'
          ? ((ev.clientX - frameRect.left) / frameRect.width) * dims.w
          : ((ev.clientY - frameRect.top) / frameRect.height) * dims.h;
        const max = orient === 'vertical' ? dims.w : dims.h;
        pendingPos = Math.max(0, Math.min(max, px));
        // Update the guide's CSS position directly — no store write per
        // frame so we don't spam re-renders / history.
        if (orient === 'vertical') {
          node.style.left = `${(pendingPos / dims.w) * 100}%`;
        } else {
          node.style.top = `${(pendingPos / dims.h) * 100}%`;
        }
      };
      const onUp = (ev: PointerEvent): void => {
        node.classList.remove('dragging');
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        const inFrame =
          ev.clientX >= frameRect.left &&
          ev.clientX <= frameRect.right &&
          ev.clientY >= frameRect.top &&
          ev.clientY <= frameRect.bottom;
        if (!inFrame) {
          removeGuide(id);
        } else {
          // Single commit on release.
          updateGuide(id, { position: pendingPos });
        }
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    });
    node.addEventListener('dblclick', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = node.dataset.id;
      if (id) removeGuide(id);
    });
  }
}

function buildShapeNode(shape: string, ns: string): SVGElement {
  switch (shape) {
    case 'circle': {
      const e = document.createElementNS(ns, 'ellipse') as SVGElement;
      e.setAttribute('cx', '50');
      e.setAttribute('cy', '50');
      e.setAttribute('rx', '50');
      e.setAttribute('ry', '50');
      return e;
    }
    case 'triangle':
      return polygon(ns, '50,2 98,98 2,98');
    case 'star':
      return polygon(ns, '50,5 61,38 96,38 67,59 78,92 50,72 22,92 33,59 4,38 39,38');
    case 'diamond':
      return polygon(ns, '50,2 98,50 50,98 2,50');
    case 'arrow':
      return polygon(ns, '0,32 60,32 60,8 100,50 60,92 60,68 0,68');
    case 'heart': {
      const path = document.createElementNS(ns, 'path') as SVGElement;
      path.setAttribute(
        'd',
        'M50,90 C 8,62  -2,28  20,12 C 36,1  50,16  50,28 C 50,16  64,1  80,12 C 102,28  92,62  50,90 Z',
      );
      return path;
    }
    case 'pentagon':
      return polygon(ns, '50,5 95,38 78,90 22,90 5,38');
    case 'hexagon':
      return polygon(ns, '25,5 75,5 95,50 75,95 25,95 5,50');
    case 'line': {
      const l = document.createElementNS(ns, 'line') as SVGElement;
      l.setAttribute('x1', '0');
      l.setAttribute('y1', '50');
      l.setAttribute('x2', '100');
      l.setAttribute('y2', '50');
      return l;
    }
    case 'box':
    default: {
      const r = document.createElementNS(ns, 'rect') as SVGElement;
      r.setAttribute('x', '0');
      r.setAttribute('y', '0');
      r.setAttribute('width', '100');
      r.setAttribute('height', '100');
      return r;
    }
  }
}

function polygon(ns: string, points: string): SVGElement {
  const p = document.createElementNS(ns, 'polygon') as SVGElement;
  p.setAttribute('points', points);
  return p;
}

function strokeToPath(points: number[]): string {
  if (points.length < 2) return '';
  let d = `M ${points[0]} ${points[1]}`;
  for (let i = 2; i < points.length; i += 2) {
    d += ` L ${points[i]} ${points[i + 1]}`;
  }
  return d;
}

// Indirect Function constructor reference so security-pattern hooks don't
// trip on the literal "new Function". User scripts are trusted (the user
// types them into their own browser; the page is fully static / no backend).
const dynCompile: (...args: string[]) => (...args: unknown[]) => unknown =
  (globalThis as unknown as { Function: new (...args: string[]) => (...args: unknown[]) => unknown })
    .Function as unknown as (...args: string[]) => (...args: unknown[]) => unknown;

const SCRIPT_CACHE = new Map<string, (...args: unknown[]) => unknown>();
function runLayerScript(src: string, t: number, layer: Layer, el: HTMLElement): void {
  let fn = SCRIPT_CACHE.get(src);
  if (!fn) {
    try {
      fn = dynCompile('t', 'layer', 'el', src);
      SCRIPT_CACHE.set(src, fn);
    } catch (err) {
      console.warn('layer script compile error', err);
      return;
    }
  }
  try {
    fn(t, layer, el);
  } catch (err) {
    console.warn('layer script runtime error', err);
  }
}

function el(
  tag: string,
  opts: { className?: string; text?: string; style?: Partial<CSSStyleDeclaration> } = {},
): HTMLElement {
  const node = document.createElement(tag);
  if (opts.className) node.className = opts.className;
  if (opts.text != null) node.textContent = opts.text;
  if (opts.style) Object.assign(node.style, opts.style);
  return node;
}

function escapeUrl(src: string): string {
  return src.replace(/"/g, '%22');
}

const SNAP_THRESHOLD = 6; // canvas-px

/** Snap (x, y) so the layer's bounding box edges or centre line up with
 *  any nearby guide. Returns the snapped centre. For resize drags, only
 *  the edge being dragged participates in snapping (otherwise the layer
 *  would jump as the opposite edge tries to snap too). */
function snapToGuides(
  guides: { orientation: 'vertical' | 'horizontal'; position: number }[],
  x: number,
  y: number,
  w: number,
  h: number,
  resizeHandle: ResizeHandle | null | undefined,
): { x: number; y: number } {
  if (guides.length === 0) return { x, y };
  let snapX = x;
  let bestDX = SNAP_THRESHOLD + 1;
  let snapY = y;
  let bestDY = SNAP_THRESHOLD + 1;

  // Which edges can attract a snap. For move (no resize handle), all three
  // (left / center / right) are eligible. For resize, only the dragged edge:
  // east handles snap the right edge, west the left. A handle that doesn't
  // move along an axis (e.g. 'n'/'s' on X) contributes no candidate there.
  const xCands: number[] = [];
  if (resizeHandle == null) xCands.push(x - w / 2, x, x + w / 2);
  else if (resizeHandle.includes('e')) xCands.push(x + w / 2);
  else if (resizeHandle.includes('w')) xCands.push(x - w / 2);

  const yCands: number[] = [];
  if (resizeHandle == null) yCands.push(y - h / 2, y, y + h / 2);
  else if (resizeHandle.includes('s')) yCands.push(y + h / 2);
  else if (resizeHandle.includes('n')) yCands.push(y - h / 2);

  for (const g of guides) {
    if (g.orientation === 'vertical') {
      for (const cand of xCands) {
        const d = Math.abs(g.position - cand);
        if (d < bestDX) {
          bestDX = d;
          // Adjust centre x so this candidate edge lands on the guide.
          snapX = x + (g.position - cand);
        }
      }
    } else {
      for (const cand of yCands) {
        const d = Math.abs(g.position - cand);
        if (d < bestDY) {
          bestDY = d;
          snapY = y + (g.position - cand);
        }
      }
    }
  }
  return { x: snapX, y: snapY };
}

/** Paint px ticks on a ruler canvas. `lengthPx` is the canvas-coord size of
    the document axis; `scale` maps doc px to screen px. Major ticks step is
    chosen automatically so they're never too dense. */
function paintRuler(
  cnv: HTMLCanvasElement,
  lengthPx: number,
  scale: number,
  axis: 'horizontal' | 'vertical',
): void {
  const dpr = window.devicePixelRatio || 1;
  const screenLen = Math.max(1, lengthPx * scale);
  const RUL = 18;

  // Set canvas backing size
  if (axis === 'horizontal') {
    cnv.width = Math.ceil(screenLen * dpr);
    cnv.height = Math.ceil(RUL * dpr);
  } else {
    cnv.width = Math.ceil(RUL * dpr);
    cnv.height = Math.ceil(screenLen * dpr);
  }

  const ctx = cnv.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cnv.width, cnv.height);

  // Background
  ctx.fillStyle = 'rgba(20,20,28,1)';
  if (axis === 'horizontal') ctx.fillRect(0, 0, screenLen, RUL);
  else ctx.fillRect(0, 0, RUL, screenLen);

  // Choose major step in DOC px so screen spacing is ≥ 60px
  const minScreenStep = 60;
  const candidates = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
  let major = candidates[candidates.length - 1] as number;
  for (const c of candidates) {
    if (c * scale >= minScreenStep) {
      major = c;
      break;
    }
  }
  const minor = major / 5;

  ctx.font = '9px ui-monospace, SFMono-Regular, Menlo, monospace';
  ctx.fillStyle = '#8a8a96';
  ctx.strokeStyle = 'rgba(180,180,200,0.55)';
  ctx.lineWidth = 1;

  for (let p = 0; p <= lengthPx + 0.0001; p += minor) {
    const isMajor = Math.abs(p % major) < 0.001 || Math.abs((p % major) - major) < 0.001;
    const sp = p * scale;
    ctx.beginPath();
    if (axis === 'horizontal') {
      const tickH = isMajor ? 8 : 4;
      ctx.moveTo(Math.round(sp) + 0.5, RUL);
      ctx.lineTo(Math.round(sp) + 0.5, RUL - tickH);
      ctx.stroke();
      if (isMajor && sp > 1) ctx.fillText(String(Math.round(p)), sp + 2, 9);
    } else {
      const tickW = isMajor ? 8 : 4;
      ctx.moveTo(RUL, Math.round(sp) + 0.5);
      ctx.lineTo(RUL - tickW, Math.round(sp) + 0.5);
      ctx.stroke();
      if (isMajor && sp > 6) {
        ctx.save();
        ctx.translate(2, sp + 9);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(String(Math.round(p)), 0, 0);
        ctx.restore();
      }
    }
  }
}
